import type { MaybeRefOrGetter } from "vue";
import { computed, readonly, ref, shallowRef, toValue } from "vue";

import { $t } from "../../../i18n";
import type { ConditionRow } from "../condition/conditionChain";
import { summarizeConditions } from "../condition/conditionSummary";
import { joinLabel, listConditionDisabledOperators } from "../condition/operators";
import type { ColFilterGroup } from "../filterModel";
import { LogicalOperator } from "../filterModel";
import type { ListColumnFilterConfig, ListOption } from "../types";
import { useFilterModelSync } from "../useFilterModelSync";
import type { ListSelection } from "./listFilterModel";
import { buildListFilterGroups, encodeSelection, parseListFilterGroups } from "./listFilterModel";
import { parsePairedListOptionId } from "./pairedListOption";
import { useListOptionsLoader } from "./useListOptionsLoader";

/** The block the dropdown shows under the toolbar. */
export type ListOptionsView = "none" | "skeleton" | "options" | "empty";

export interface ListFilterOptions {
  colKey: MaybeRefOrGetter<string>;
  config: MaybeRefOrGetter<ListColumnFilterConfig>;
  model: MaybeRefOrGetter<ColFilterGroup[] | null>;
  onModelChange: (model: ColFilterGroup[] | null) => void;
}

/** Sends no selection, like everything checked, but keeps the boxes unchecked while the list is open. */
const NOTHING_CHECKED: ListSelection = { mode: "include", ids: [], blank: false };

/** Up to this many checked values the summary names them; "All except …" reads oddly on a short list. */
const MAX_LISTED_CHECKED = 3;

function isNothingChecked(selection: ListSelection | null) {
  return selection?.mode === "include" && selection.ids.length === 0 && !selection.blank;
}

export function useListFilter(options: ListFilterOptions) {
  const config = computed(() => toValue(options.config));
  const loader = useListOptionsLoader({
    options: () => config.value.options,
    loadOptions: () => config.value.loadOptions,
  });
  const listOptions = loader.options;
  const blankOption = computed(() => Boolean(config.value.blankOption));
  const keys = computed(() => ({
    searchColKey: config.value.searchColKey,
    pairColKey: config.value.pairColKey,
    blankOption: blankOption.value,
  }));

  /** `null`: every box is checked. */
  const selection = shallowRef<ListSelection | null>(null);
  const conditionRows = ref<ConditionRow[]>([]);
  const listSearchTerm = ref("");
  const activeSelection = computed(() => (isNothingChecked(selection.value) ? null : selection.value));

  const sync = useFilterModelSync({
    model: options.model,
    onModelChange: options.onModelChange,
    build: () =>
      buildListFilterGroups({
        colKey: toValue(options.colKey),
        selection: activeSelection.value,
        conditionRows: conditionRows.value,
        ...keys.value,
      }),
    adopt: (model) => {
      const state = parseListFilterGroups(model, toValue(options.colKey), keys.value);
      selection.value = state.selection;
      conditionRows.value = state.conditionRows;
    },
  });

  function orphanLabel(id: string) {
    return config.value.pairColKey ? (parsePairedListOptionId(id)?.term ?? id) : id;
  }

  const orphanOptions = computed(() => {
    const known = new Set(listOptions.value.map((option) => option.id));
    return (selection.value?.ids ?? [])
      .filter((id) => !known.has(id))
      .map((id): ListOption => ({ id, label: orphanLabel(id) }));
  });

  const rowOptions = computed(() => [...orphanOptions.value, ...listOptions.value]);
  const universe = computed(() => rowOptions.value.map((option) => option.id));

  function matchesSearch(label: string) {
    return !listSearchTerm.value || label.toLowerCase().includes(listSearchTerm.value.toLowerCase());
  }

  const filteredOptions = computed(() => rowOptions.value.filter((option) => matchesSearch(option.label)));
  const blankLabel = computed(() => $t("columnFilter.list.blank"));
  const blankOptionVisible = computed(() => blankOption.value && matchesSearch(blankLabel.value));

  function isSelected(id: string) {
    const current = selection.value;
    if (!current) return true;
    return current.mode === "include" ? current.ids.includes(id) : !current.ids.includes(id);
  }

  const blankChecked = computed(() => selection.value?.blank ?? true);
  const checkedIds = computed(() => universe.value.filter(isSelected));
  const itemCount = computed(() => universe.value.length + (blankOption.value ? 1 : 0));
  const checkedCount = computed(() => checkedIds.value.length + (blankOption.value && blankChecked.value ? 1 : 0));
  const allSelected = computed(() => itemCount.value > 0 && checkedCount.value === itemCount.value);
  const someSelected = computed(() => checkedCount.value > 0 && !allSelected.value);

  const checkedLabels = computed(() => [
    ...rowOptions.value.filter((option) => isSelected(option.id)).map((option) => option.label),
    ...(blankOption.value && blankChecked.value ? [blankLabel.value] : []),
  ]);

  const selectionSummary = computed(() => {
    const current = activeSelection.value;
    if (!current) return "";
    const labelById = new Map(rowOptions.value.map((option) => [option.id, option.label]));
    const labels = current.ids.map((id) => labelById.get(id) ?? id);
    const exclude = current.mode === "exclude";
    if (blankOption.value && current.blank !== exclude) labels.push(blankLabel.value);
    if (!exclude) return labels.join(", ");
    // Until the options load, an "all except" selection knows only what it leaves out.
    const checked = checkedLabels.value;
    const namesChecked =
      loader.ready.value &&
      checked.length > 0 &&
      (checked.length <= MAX_LISTED_CHECKED || checked.length <= labels.length);
    return namesChecked ? checked.join(", ") : $t("columnFilter.list.allExcept", { values: labels.join(", ") });
  });
  const conditionSummary = computed(() => summarizeConditions(conditionRows.value));
  const summaryText = computed(() =>
    [selectionSummary.value, conditionSummary.value].filter(Boolean).join(` ${joinLabel(LogicalOperator.AND)} `),
  );

  const hasActiveFilter = computed(() => activeSelection.value !== null || conditionRows.value.length > 0);

  // Conditions saved before the column opted in stay editable.
  const conditionsEnabled = computed(() => Boolean(config.value.textConditions) || conditionRows.value.length > 0);
  const conditionDisabledOperators = computed(() => listConditionDisabledOperators(config.value));

  const optionsView = computed<ListOptionsView>(() => {
    if (loader.skeleton.value) return "skeleton";
    // Clicks wait for a load that has not succeeded, so rows shown now could not be clicked.
    if (loader.busy.value || loader.failed.value) return "none";
    if (rowOptions.value.length > 0 || (blankOption.value && loader.ready.value)) return "options";
    return "empty";
  });

  function select(checked: readonly string[], blank: boolean, items: readonly string[]) {
    const next = encodeSelection(checked, blank, items, {
      blankOption: blankOption.value,
      multiValue: config.value.multiValue,
    });
    selection.value = next === "none" ? NOTHING_CHECKED : next;
    sync.stage();
  }

  // "All except" needs every option, so clicks wait for the load.
  /** Clears everything when everything is checked, else checks everything (a partial selection too). */
  function toggleSelectAll() {
    if (!loader.ready.value) return;
    selection.value = allSelected.value ? NOTHING_CHECKED : null;
    sync.apply();
  }

  /** An unmatched id leaves the list once toggled: unchecked in "only", checked again in "all except". */
  function toggleItem(id: string, checked: boolean) {
    if (!loader.ready.value || isSelected(id) === checked) return;
    const orphan = orphanOptions.value.some((option) => option.id === id);
    const items = orphan ? universe.value.filter((item) => item !== id) : universe.value;
    select(
      items.filter((item) => (item === id ? checked : isSelected(item))),
      blankChecked.value,
      items,
    );
  }

  function toggleBlank(checked: boolean) {
    if (!loader.ready.value || !blankOption.value || blankChecked.value === checked) return;
    select(checkedIds.value, checked, universe.value);
  }

  function changeConditions(rows: readonly ConditionRow[]) {
    conditionRows.value = [...rows];
    sync.stage();
  }

  /** Applies a staged edit; a list left with nothing checked opens with everything checked next time. */
  function endEditing() {
    sync.flush();
    if (isNothingChecked(selection.value)) selection.value = null;
  }

  function clear() {
    selection.value = null;
    conditionRows.value = [];
    sync.clear();
  }

  return {
    selection: readonly(selection),
    conditionRows: readonly(conditionRows),
    isSelected,
    blankChecked,
    blankLabel,
    blankOptionVisible,
    listSearchTerm,
    filteredOptions,
    optionsView,
    optionsSkeleton: loader.skeleton,
    loadOptions: loader.load,
    allSelected,
    someSelected,
    summaryText,
    hasActiveFilter,
    conditionsEnabled,
    conditionDisabledOperators,
    toggleSelectAll,
    toggleItem,
    toggleBlank,
    changeConditions,
    flush: sync.flush,
    endEditing,
    clear,
  };
}
